import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, Phone, Mail } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    phone: '',
    email: '',
    medical_history: ''
  });

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      const data = await api.getPatients();
      setPatients(data);
    } catch (error) {
      toast.error('Erro ao carregar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      toast.error('O nome é obrigatório.');
      return;
    }
    try {
      // Send null for empty optional fields so Pydantic accepts them
      const payload = {
        name: formData.name.trim(),
        date_of_birth: formData.date_of_birth?.trim() || null,
        phone: formData.phone?.trim() || null,
        email: formData.email?.trim() || null,
        medical_history: formData.medical_history?.trim() || null
      };
      await api.createPatient(payload);
      toast.success('Paciente criado com sucesso!');
      setIsDialogOpen(false);
      setFormData({
        name: '',
        date_of_birth: '',
        phone: '',
        email: '',
        medical_history: ''
      });
      loadPatients();
    } catch (error) {
      toast.error('Erro ao criar paciente');
    }
  };

  const filteredPatients = patients.filter((patient) =>
    patient.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="patients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-8">
          <div>
            <h1 className={`text-2xl sm:text-3xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>
              Pacientes
            </h1>
            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Gerencie seus pacientes</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto bg-primary hover:bg-primary-hover" data-testid="new-patient-btn">
                <Plus className="h-5 w-5 mr-2" />
                Novo Paciente
              </Button>
            </DialogTrigger>
            <DialogContent className={`sm:max-w-[500px] ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Novo Paciente</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="new-patient-form">
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'} bg-slate-100 dark:bg-slate-800/50 p-3 rounded-md`}>
                  💡 Apenas o <strong>Nome</strong> é obrigatório. Os demais campos podem ser preenchidos depois.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="name" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Nome Completo *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Maria Silva"
                    required
                    className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}
                    data-testid="patient-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_of_birth" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Data de Nascimento (opcional)</Label>
                  <Input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}
                    data-testid="patient-dob-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Telefone (opcional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(11) 99999-9999"
                      className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}
                      data-testid="patient-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Email (opcional)</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@exemplo.com"
                      className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}
                      data-testid="patient-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="medical_history" className={isDark ? 'text-slate-300' : 'text-slate-700'}>Histórico Médico (opcional)</Label>
                  <textarea
                    id="medical_history"
                    name="medical_history"
                    value={formData.medical_history}
                    onChange={handleChange}
                    className={`w-full min-h-[80px] px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                      isDark 
                        ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' 
                        : 'border-slate-200 text-slate-900'
                    }`}
                    placeholder="Cirurgias, medicamentos, condições prévias..."
                    data-testid="patient-history-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className={`flex-1 ${isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}`}
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 bg-primary hover:bg-primary-hover" data-testid="save-patient-btn">
                    Salvar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <Input
              type="text"
              placeholder="Buscar pacientes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-10 ${isDark ? 'bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500' : ''}`}
              data-testid="search-patients-input"
            />
          </div>
        </div>

        {/* Patients List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} rounded-xl border p-12 text-center`}>
            <p className={isDark ? 'text-slate-400' : 'text-slate-600'} mb-4>
              {search ? 'Nenhum paciente encontrado' : 'Nenhum paciente cadastrado ainda'}
            </p>
            {!search && (
              <Button
                onClick={() => setIsDialogOpen(true)}
                className="bg-primary hover:bg-primary-hover"
              >
                Adicionar primeiro paciente
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredPatients.map((patient, index) => (
              <Link
                key={patient.id}
                to={`/patients/${patient.id}`}
                className={`${isDark ? 'bg-slate-800/50 border-slate-700 hover:border-primary/50' : 'bg-white border-slate-200 hover:border-primary/50'} p-6 rounded-xl border hover:shadow-md transition-all`}
                data-testid={index === 0 ? 'patient-item' : `patient-card-${patient.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-2`}>{patient.name}</h3>
                    <div className="space-y-1">
                      {patient.phone && (
                        <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          <Phone className="h-4 w-4" />
                          {patient.phone}
                        </div>
                      )}
                      {patient.email && (
                        <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                          <Mail className="h-4 w-4" />
                          {patient.email}
                        </div>
                      )}
                    </div>
                  </div>
                  {patient.date_of_birth && (
                    <div className="text-right">
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Nascimento</p>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        {new Date(patient.date_of_birth).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
